import React, { useState, useEffect } from 'react';
import { ref, get, set, update, remove, onValue } from 'firebase/database';
import { database } from '../firebase';
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import '../styles/global.css';

const AdminTutorialsPanel = () => {
    // R2 credentials from secure env variables
    const r2AccessKey = import.meta.env.VITE_R2_ACCESS_KEY || '';
    const r2SecretKey = import.meta.env.VITE_R2_SECRET_KEY || '';
    const r2PublicUrl = import.meta.env.VITE_R2_PUBLIC_URL || '';
    const r2AccountId = import.meta.env.VITE_R2_ACCOUNT_ID || '';

    // Form and List state
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Todos');
    const [categories, setCategories] = useState(['Todos', 'Tutoriales Vortex', 'Conocimientos Marineros']);

    // Upload & Form state
    const [editingVideo, setEditingVideo] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('Tutoriales Vortex');
    const [customCategory, setCustomCategory] = useState('');
    const [sourceType, setSourceType] = useState('r2'); // 'r2', 'youtube', or 'tiktok'
    const [videoId, setVideoId] = useState(''); // YouTube/TikTok Video ID
    const [videoUrl, setVideoUrl] = useState(''); // R2 Video URL
    const [duration, setDuration] = useState('3:00');
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [bucketName, setBucketName] = useState(localStorage.getItem('r2_bucket_name') || 'ascensosapp');

    // UI state
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [saving, setSaving] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [fileToUpload, setFileToUpload] = useState(null);

    // Fetch active videos from Firebase Realtime Database
    useEffect(() => {
        const videosRef = ref(database, 'config/youtube');
        const unsub = onValue(videosRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const videoList = Object.entries(data).map(([id, val]) => {
                    if (typeof val === 'string') {
                        return {
                            id,
                            videoId: val,
                            title: `Tutorial ${id}`,
                            description: 'Video tutorial de la aplicación',
                            thumbnailUrl: `https://img.youtube.com/vi/${val}/mqdefault.jpg`,
                            category: 'Tutoriales Vortex',
                            duration: '0:00',
                            videoUrl: ''
                        };
                    }
                    return {
                        id,
                        ...val,
                        thumbnailUrl: val.thumbnailUrl || (val.videoId ? `https://img.youtube.com/vi/${val.videoId}/mqdefault.jpg` : '')
                    };
                });
                setVideos(videoList);

                // Dynamically update categories list
                const dynamicCats = ['Todos', ...new Set(videoList.map(v => v.category).filter(Boolean))];
                setCategories(dynamicCats.includes('Tutoriales Vortex') && dynamicCats.includes('Conocimientos Marineros')
                    ? dynamicCats
                    : [...new Set([...dynamicCats, 'Tutoriales Vortex', 'Conocimientos Marineros'])]
                );
            } else {
                setVideos([]);
            }
            setLoading(false);
        });

        return () => unsub();
    }, []);

    // Save bucket name in local storage for convenience
    useEffect(() => {
        localStorage.setItem('r2_bucket_name', bucketName);
    }, [bucketName]);

    // Handle video file selection
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 200 * 1024 * 1024) { // 200MB limit check
                alert("El archivo excede el límite recomendado de 200MB. Por favor optimiza el video antes de subirlo.");
                return;
            }
            setFileToUpload(file);
            setUploadError(null);
            
            // Auto fill duration (estimate or default) and sanitize title if empty
            if (!title) {
                const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ");
                setTitle(cleanName.charAt(0).toUpperCase() + cleanName.slice(1));
            }
        }
    };

    // Upload direct file to Cloudflare R2
    const handleR2Upload = async () => {
        if (!fileToUpload) {
            alert("Por favor selecciona un video (.mp4) primero.");
            return;
        }
        if (!r2AccessKey || !r2SecretKey || !r2AccountId || !r2PublicUrl) {
            alert("Error: Faltan llaves de acceso de R2 en las variables de entorno (.env). Revisa tu configuración.");
            return;
        }

        setUploading(true);
        setUploadProgress(15); // Start progress estimation
        setUploadError(null);

        try {
            // Generate clean filename
            const cleanFileName = `${Date.now()}_${fileToUpload.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.]/g, '')}`;
            
            // Initialize AWS S3 client for Cloudflare R2 compatibility
            const s3Client = new S3Client({
                region: "auto",
                endpoint: `https://${r2AccountId}.r2.cloudflarestorage.com`,
                credentials: {
                    accessKeyId: r2AccessKey,
                    secretAccessKey: r2SecretKey,
                },
            });

            setUploadProgress(45);

            // Upload object to R2
            const command = new PutObjectCommand({
                Bucket: bucketName,
                Key: cleanFileName,
                Body: fileToUpload,
                ContentType: fileToUpload.type,
            });

            await s3Client.send(command);
            setUploadProgress(85);

            // Generate clean public access URL
            const finalPublicUrl = `${r2PublicUrl.endsWith('/') ? r2PublicUrl.slice(0, -1) : r2PublicUrl}/${cleanFileName}`;
            setVideoUrl(finalPublicUrl);
            setVideoId(''); // Clear youtube id if R2 was uploaded
            setUploadProgress(100);

            // Auto assign a default naval video thumbnail if empty
            if (!thumbnailUrl) {
                setThumbnailUrl('https://images.unsplash.com/photo-1507682531662-421b17ac4f83?auto=format&fit=crop&w=400&q=80'); // Yacht / sea premium background
            }

            alert("¡Video subido a Cloudflare R2 con éxito! Recuerda guardar el formulario.");
        } catch (error) {
            console.error("R2 Upload Error:", error);
            setUploadError(error.message || "Error desconocido al subir a R2.");
            alert("Error al subir a Cloudflare R2: " + (error.message || "Verifica CORS o tus credenciales en el bucket."));
        } finally {
            setUploading(false);
        }
    };

    // Save or update tutorial record in Firebase
    const handleSaveVideo = async (e) => {
        e.preventDefault();
        
        if (!title.trim() || !description.trim()) {
            alert("Por favor completa el título y la descripción.");
            return;
        }

        const finalCategory = category === 'custom' ? customCategory.trim() : category;
        if (!finalCategory) {
            alert("Por favor selecciona o escribe una categoría.");
            return;
        }

        if (sourceType === 'youtube' && !videoId.trim()) {
            alert("Por favor ingresa un ID de video de YouTube de 11 caracteres.");
            return;
        }

        if (sourceType === 'tiktok' && !videoId.trim()) {
            alert("Por favor ingresa un ID o URL de video de TikTok.");
            return;
        }

        if (sourceType === 'r2' && !videoUrl.trim()) {
            alert("Por favor ingresa una URL de Cloudflare R2 o sube un archivo.");
            return;
        }

        setSaving(true);

        try {
            const videoData = {
                title: title.trim(),
                description: description.trim(),
                category: finalCategory,
                duration: duration.trim() || '3:00',
                videoUrl: sourceType === 'r2' ? videoUrl.trim() : '',
                videoId: (sourceType === 'youtube' || sourceType === 'tiktok') ? videoId.trim() : '',
                source: sourceType,
                thumbnailUrl: thumbnailUrl.trim() || (sourceType === 'youtube' ? `https://img.youtube.com/vi/${videoId.trim()}/mqdefault.jpg` : sourceType === 'tiktok' ? 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&w=400&q=80' : 'https://images.unsplash.com/photo-1507682531662-421b17ac4f83?auto=format&fit=crop&w=400&q=80')
            };

            const videoKey = editingVideo ? editingVideo.id : `video_${Date.now()}`;
            const videoRef = ref(database, `config/youtube/${videoKey}`);
            
            await set(videoRef, videoData);
            
            // Reset state
            handleResetForm();
            alert(editingVideo ? "¡Video tutorial actualizado correctamente!" : "¡Video tutorial creado correctamente!");
        } catch (error) {
            console.error("Save Error:", error);
            alert("Error al guardar tutorial: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Load video details into form for editing
    const handleEdit = (video) => {
        setEditingVideo(video);
        setTitle(video.title || '');
        setDescription(video.description || '');
        setDuration(video.duration || '3:00');
        setThumbnailUrl(video.thumbnailUrl || '');
        
        if (categories.includes(video.category)) {
            setCategory(video.category);
            setCustomCategory('');
        } else {
            setCategory('custom');
            setCustomCategory(video.category || '');
        }

        const determinedSource = video.source || (video.videoUrl ? 'r2' : 'youtube');
        setSourceType(determinedSource);
        
        if (determinedSource === 'r2') {
            setVideoUrl(video.videoUrl || '');
            setVideoId('');
        } else if (determinedSource === 'tiktok') {
            setVideoUrl('');
            setVideoId(video.videoId || '');
        } else {
            setVideoUrl('');
            setVideoId(video.videoId || '');
        }

        // Scroll to form smoothly
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Delete a video tutorial
    const handleDelete = async (video) => {
        if (!window.confirm(`¿Estás seguro de que deseas eliminar el tutorial "${video.title}"?`)) {
            return;
        }

        try {
            const videoRef = ref(database, `config/youtube/${video.id}`);
            await remove(videoRef);
            alert("Video tutorial eliminado correctamente.");
        } catch (error) {
            console.error("Delete Error:", error);
            alert("Error al eliminar el video tutorial.");
        }
    };

    // Clear and reset uploader form
    const handleResetForm = () => {
        setEditingVideo(null);
        setTitle('');
        setDescription('');
        setCategory('Tutoriales Vortex');
        setCustomCategory('');
        setSourceType('r2');
        setVideoId('');
        setVideoUrl('');
        setDuration('3:00');
        setThumbnailUrl('');
        setFileToUpload(null);
        setUploadProgress(0);
        setUploadError(null);
    };

    // Filtered list computed dynamically
    const filteredVideos = videos.filter(video => {
        const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              video.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'Todos' || video.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="admin-tutorials-panel" style={{ color: '#fff', fontFamily: 'sans-serif' }}>
            
            {/* STATS HEADER */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#00E5FF' }}>{videos.length}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>Videos en la App</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#4CAF50' }}>
                        {videos.filter(v => v.videoUrl).length}
                    </div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>Directos en R2 (Ad-Free)</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#FFEB3B' }}>
                        {videos.filter(v => !v.videoUrl).length}
                    </div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>Vinculados de YouTube</div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '15px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: r2AccountId ? '#4CAF50' : '#F44336' }}>
                        {r2AccountId ? '🟢 CONECTADO' : '🔴 SIN LLAVES'}
                    </div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, marginTop: '4px' }}>Estado Cloudflare R2</div>
                </div>
            </div>

            {/* FORM SECTION */}
            <div style={{ background: 'rgba(10, 25, 47, 0.65)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '20px', marginBottom: '30px', backdropFilter: 'blur(10px)' }}>
                <h3 style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '10px', marginTop: 0, marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>🎬 {editingVideo ? `Editar Tutorial: ${editingVideo.title}` : 'Crear Nuevo Video Tutorial'}</span>
                    {editingVideo && (
                        <button onClick={handleResetForm}  className="adm-btn adm-btn--secondary">
                            Cancelar Edición
                        </button>
                    )}
                </h3>

                <form onSubmit={handleSaveVideo} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {/* LEFT COLUMN */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label  className="adm-label">Título del Tutorial:</label>
                            <input 
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Ej: Cómo realizar un simulador de examen"
                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label  className="adm-label">Descripción:</label>
                            <textarea 
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Explica detalladamente qué aprenderá el usuario en este video..."
                                rows="3"
                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none', resize: 'vertical' }}
                                required
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label  className="adm-label">Categoría:</label>
                                <select 
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    style={{ background: '#0a192f', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                >
                                    <option value="Tutoriales Vortex">Tutoriales Vortex</option>
                                    <option value="Conocimientos Marineros">Conocimientos Marineros</option>
                                    <option value="custom">-- Otra Categoría --</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label  className="adm-label">Duración (min:seg):</label>
                                <input 
                                    type="text"
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    placeholder="Ej: 5:45"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                />
                            </div>
                        </div>

                        {category === 'custom' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                <label  className="adm-label">Escribe Nueva Categoría:</label>
                                <input 
                                    type="text"
                                    value={customCategory}
                                    onChange={(e) => setCustomCategory(e.target.value)}
                                    placeholder="Ej: Maniobras Avanzadas"
                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label  className="adm-label">URL de Miniatura (Opcional):</label>
                            <input 
                                type="text"
                                value={thumbnailUrl}
                                onChange={(e) => setThumbnailUrl(e.target.value)}
                                placeholder="https://example.com/imagen.jpg (En blanco usa thumbnail por defecto)"
                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                            />
                        </div>
                    </div>

                    {/* RIGHT COLUMN: UPLOAD & SOURCE */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '20px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label  className="adm-label">Origen del Video:</label>
                            <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                                    <input 
                                        type="radio" 
                                        name="sourceType" 
                                        value="r2" 
                                        checked={sourceType === 'r2'}
                                        onChange={() => setSourceType('r2')}
                                    />
                                    Cloudflare R2 Directo
                                </label>
                                <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                                    <input 
                                        type="radio" 
                                        name="sourceType" 
                                        value="youtube" 
                                        checked={sourceType === 'youtube'}
                                        onChange={() => setSourceType('youtube')}
                                    />
                                    YouTube Link
                                </label>
                                <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                                    <input 
                                        type="radio" 
                                        name="sourceType" 
                                        value="tiktok" 
                                        checked={sourceType === 'tiktok'}
                                        onChange={() => setSourceType('tiktok')}
                                    />
                                    TikTok Link / ID
                                </label>
                            </div>
                        </div>

                        {sourceType === 'youtube' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label  className="adm-label">ID de Video de YouTube:</label>
                                    <input 
                                        type="text"
                                        value={videoId}
                                        onChange={(e) => setVideoId(e.target.value)}
                                        placeholder="Ej: dQw4w9WgXcQ (11 caracteres)"
                                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                    />
                                </div>
                                <p style={{ fontSize: '0.75rem', opacity: 0.6, margin: 0 }}>
                                    💡 El ID de YouTube se puede obtener del link original, ej: youtube.com/watch?v=<b>dQw4w9WgXcQ</b>
                                </p>
                            </div>
                        ) : sourceType === 'tiktok' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label  className="adm-label">ID o URL de Video de TikTok:</label>
                                    <input 
                                        type="text"
                                        value={videoId}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const match = val.match(/\/video\/(\d+)/);
                                            if (match && match[1]) {
                                                setVideoId(match[1]);
                                            } else {
                                                setVideoId(val.trim());
                                            }
                                        }}
                                        placeholder="Ej: 7351234567890123456 o pega la URL del video"
                                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', outline: 'none' }}
                                    />
                                </div>
                                <p style={{ fontSize: '0.75rem', opacity: 0.6, margin: 0 }}>
                                    💡 Puedes pegar el ID de 19 dígitos o la URL completa de TikTok (ej: tiktok.com/@usuario/video/7351...)
                                </p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', padding: '15px', background: 'rgba(0, 229, 255, 0.03)', borderRadius: '10px', border: '1px dashed rgba(0, 229, 255, 0.2)' }}>
                                
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label  className="adm-label">Nombre del Bucket R2:</label>
                                        <input 
                                            type="text"
                                            value={bucketName}
                                            onChange={(e) => setBucketName(e.target.value)}
                                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '6px 10px', color: '#fff', fontSize: '0.85rem', outline: 'none' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                        <label  className="adm-label">R2 Cuenta / Endpoint:</label>
                                        <input 
                                            type="text"
                                            value={r2AccountId ? `${r2AccountId.slice(0,8)}...` : 'Sin configurar'}
                                            disabled
                                             className="adm-input"
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label  className="adm-label">Subir Video a R2:</label>
                                    <input 
                                        type="file"
                                        accept="video/*"
                                        onChange={handleFileChange}
                                         className="adm-input"
                                    />
                                </div>

                                {fileToUpload && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                                        <button
                                            type="button"
                                            onClick={handleR2Upload}
                                            disabled={uploading}
                                             className="adm-btn adm-btn--secondary"
                                        >
                                            {uploading ? 'Subiendo...' : '🚀 Iniciar Subida'}
                                        </button>
                                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                                            {(fileToUpload.size / (1024 * 1024)).toFixed(1)} MB
                                        </span>
                                    </div>
                                )}

                                {uploading && (
                                    <div style={{ marginTop: '5px' }}>
                                        <div style={{ background: 'rgba(255,255,255,0.1)', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ background: '#00E5FF', width: `${uploadProgress}%`, height: '100%', transition: 'width 0.3s' }}></div>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.8, display: 'flex', justifyContent: 'space-between' }}>
                                            <span>Subiendo a Cloudflare R2...</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                    </div>
                                )}

                                {uploadError && (
                                    <div style={{ color: '#FF5252', fontSize: '0.8rem', margin: 0 }}>
                                        ⚠️ {uploadError}
                                    </div>
                                )}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                                    <label  className="adm-label">URL Directa del Video (R2):</label>
                                    <input 
                                        type="text"
                                        value={videoUrl}
                                        onChange={(e) => setVideoUrl(e.target.value)}
                                        placeholder="https://pub-...r2.dev/video.mp4 (Se llena automáticamente)"
                                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '10px', color: '#fff', fontSize: '0.85rem', outline: 'none' }}
                                    />
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 'auto', display: 'flex', gap: '15px', paddingTop: '15px' }}>
                            <button
                                type="submit"
                                disabled={saving}
                                 style={{  flex: 1,  display: 'flex',  justifyContent: 'center',  alignItems: 'center',  gap: '8px'  }} className="adm-btn adm-btn--secondary"
                            >
                                {saving ? 'Guardando...' : '💾 Guardar Tutorial'}
                            </button>
                            <button
                                type="button"
                                onClick={handleResetForm}
                                 className="adm-btn adm-btn--secondary"
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* LIST SEARCH AND FILTERS */}
            <div style={{ background: 'rgba(10, 25, 47, 0.45)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '12px 15px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', flex: '1 1 auto', minWidth: '200px' }}>
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            style={{
                                padding: '5px 14px',
                                background: selectedCategory === cat ? '#00E5FF' : 'rgba(255,255,255,0.06)',
                                color: selectedCategory === cat ? '#0a192f' : '#fff',
                                fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                                border: 'none',
                                borderRadius: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                fontSize: '0.8rem',
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: '0 1 auto' }}>
                    <input 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="🔍 Buscar..."
                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '16px', padding: '6px 14px', color: '#fff', fontSize: '0.85rem', outline: 'none', width: '160px' }}
                    />

                    <div style={{ display: 'flex', gap: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '6px', padding: '2px' }}>
                        <button
                            onClick={() => setViewMode('grid')}
                            style={{
                                padding: '5px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                                background: viewMode === 'grid' ? '#00E5FF' : 'transparent',
                                color: viewMode === 'grid' ? '#0a192f' : '#94a3b8',
                                fontWeight: viewMode === 'grid' ? 'bold' : 'normal',
                                fontSize: '0.8rem', transition: 'all 0.2s',
                            }}
                        >▦</button>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                padding: '5px 10px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                                background: viewMode === 'list' ? '#00E5FF' : 'transparent',
                                color: viewMode === 'list' ? '#0a192f' : '#94a3b8',
                                fontWeight: viewMode === 'list' ? 'bold' : 'normal',
                                fontSize: '0.8rem', transition: 'all 0.2s',
                            }}
                        >☰</button>
                    </div>
                </div>
            </div>

            {/* VIDEOS LIST */}
            <div style={{ background: 'rgba(10, 25, 47, 0.45)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>Cargando video tutoriales...</div>
                ) : filteredVideos.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', opacity: 0.7 }}>No se encontraron video tutoriales con los filtros seleccionados.</div>
                ) : viewMode === 'grid' ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '16px',
                        padding: '16px',
                    }}>
                        {filteredVideos.map(video => (
                            <div key={video.id} style={{
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.2s',
                            }}>
                                <img 
                                    src={video.thumbnailUrl} 
                                    alt={video.title} 
                                    style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                                />
                                <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '0.95rem', color: '#fff' }}>{video.title}</div>
                                    <div style={{ opacity: 0.6, fontSize: '0.8rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>{video.description}</div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <span style={{ background: 'rgba(255,255,255,0.08)', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem' }}>
                                                {video.category}
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: '#00E5FF' }}>⏱️ {video.duration}</span>
                                        </div>
                                        {video.source === 'r2' || (!video.source && video.videoUrl) ? (
                                            <span style={{ color: '#00E5FF', fontSize: '0.75rem' }}>☁️ R2</span>
                                        ) : video.source === 'tiktok' ? (
                                            <span style={{ color: '#FF5722', fontSize: '0.75rem' }}>🎵 TikTok</span>
                                        ) : (
                                            <span style={{ color: '#FFEB3B', fontSize: '0.75rem' }}>📺 YouTube</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                        <button onClick={() => handleEdit(video)} style={{
                                            flex: 1, background: 'rgba(0, 229, 255, 0.1)', border: '1px solid #00E5FF',
                                            color: '#00E5FF', padding: '6px 0', borderRadius: '6px', cursor: 'pointer',
                                            fontSize: '0.8rem', transition: 'all 0.2s',
                                        }}>✏️ Editar</button>
                                        <button onClick={() => handleDelete(video)} style={{
                                            flex: 1, background: 'rgba(244, 67, 54, 0.1)', border: '1px solid #F44336',
                                            color: '#F44336', padding: '6px 0', borderRadius: '6px', cursor: 'pointer',
                                            fontSize: '0.8rem', transition: 'all 0.2s',
                                        }}>🗑️ Borrar</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '10px',
                        padding: '12px',
                    }}>
                        {filteredVideos.map(video => (
                            <div key={video.id} style={{
                                display: 'flex',
                                gap: '10px',
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: '10px',
                                padding: '10px',
                                alignItems: 'center',
                                transition: 'all 0.2s',
                            }}>
                                <img 
                                    src={video.thumbnailUrl} 
                                    alt={video.title} 
                                    style={{ width: '80px', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
                                />
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{video.title}</div>
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem' }}>{video.category}</span>
                                        <span style={{ fontSize: '0.7rem', color: '#00E5FF' }}>⏱️ {video.duration}</span>
                                        {video.source === 'r2' || (!video.source && video.videoUrl) ? (
                                            <span style={{ color: '#00E5FF', fontSize: '0.7rem' }}>☁️</span>
                                        ) : video.source === 'tiktok' ? (
                                            <span style={{ color: '#FF5722', fontSize: '0.7rem' }}>🎵</span>
                                        ) : (
                                            <span style={{ color: '#FFEB3B', fontSize: '0.7rem' }}>📺</span>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                                        <button onClick={() => handleEdit(video)} style={{
                                            flex: 1, background: 'rgba(0, 229, 255, 0.1)', border: '1px solid #00E5FF',
                                            color: '#00E5FF', padding: '4px 0', borderRadius: '5px', cursor: 'pointer',
                                            fontSize: '0.7rem',
                                        }}>✏️ Editar</button>
                                        <button onClick={() => handleDelete(video)} style={{
                                            flex: 1, background: 'rgba(244, 67, 54, 0.1)', border: '1px solid #F44336',
                                            color: '#F44336', padding: '4px 0', borderRadius: '5px', cursor: 'pointer',
                                            fontSize: '0.7rem',
                                        }}>🗑️ Borrar</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
    );
};

export default AdminTutorialsPanel;
